// src/components/multi-select.tsx

import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { CheckIcon, XCircle, XIcon, WandSparkles } from "lucide-react";

import ArrowBottomIcon from "../../assets/svgs/common/arrow-bottom.svg";

import { cn } from "../../utils/utils";
import { Separator } from "./separator";
import { Button } from "./button";
import { Badge } from "./badge";
import { Popover, PopoverContent, PopoverTrigger } from "./popover";
import {
	Command,
	CommandEmpty,
	CommandGroup,
	CommandItem,
	CommandList,
	CommandSeparator,
} from "./command";
import Spinner from "../spinner.components";
import type { ILocationMinimal } from "../../types/models/location.models.types";
import { Input } from "./input";

/**
 * Variants for the multi-select component to handle different styles.
 * Uses class-variance-authority (cva) to define different styles based on "variant" prop.
 */
const multiSelectVariants = cva(
	"m-1 transition hover:-translate-y-1 duration-300 delay-150 ease-in-out hover:scale-110",
	{
		variants: {
			variant: {
				default:
					"border-foreground/10 text-foreground bg-card hover:bg-card/80",
				secondary:
					"border-foreground/10 bg-secondary text-secondary-foreground hover:bg-secondary/80",
				destructive:
					"border-transparent bg-destructive text-destructive-foreground hover:bg-destructive/80",
				inverted: "inverted",
			},
		},
		defaultVariants: {
			variant: "default",
		},
	},
);

/**
 * Props for MultiSelect component
 */
interface MultiSelectProps
	extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, "defaultValue">,
		VariantProps<typeof multiSelectVariants> {
	/**
	 * An array of option objects to be displayed in the multi-select component.
	 * Each option object has a label, value, and an optional icon.
	 */
	options: {
		placeId: string;
		address: string;
	}[];

	/**
	 * Callback function triggered when the selected values change.
	 * Receives an array of the new selected values.
	 */
	onValueChange: (
		value: {
			placeId: string;
			address: string;
		}[],
	) => void;

	/** The default selected values when the component mounts. */
	defaultValue?: {
		placeId: string;
		address: string;
	}[];

	/**
	 * Placeholder text to be displayed when no values are selected.
	 * Optional, defaults to "Select options".
	 */
	placeholder?: string;

	/**
	 * Animation duration in seconds for the visual effects (e.g., bouncing badges).
	 * Optional, defaults to 0 (no animation).
	 */
	animation?: number;

	/**
	 * Maximum number of items to display. Extra selected items will be summarized.
	 * Optional, defaults to 3.
	 */
	maxCount?: number;

	/**
	 * The modality of the popover. When set to true, interaction with outside elements
	 * will be disabled and only popover content will be visible to screen readers.
	 * Optional, defaults to false.
	 */
	modalPopover?: boolean;

	/**
	 * If true, renders the multi-select component as a child of another component.
	 * Optional, defaults to false.
	 */
	asChild?: boolean;

	/**
	 * Additional class names to apply custom styles to the multi-select component.
	 * Optional, can be used to add custom styles.
	 */
	className?: string;

	/** Fetcher function to load options asynchronously. */
	fetcher?: (searchQuery: string) => void;

	/** Indicates whether options are being fetched. */
	fetching?: boolean;
}

export const MultiSelectLocationSearch = React.forwardRef<
	HTMLButtonElement,
	MultiSelectProps
>(
	(
		{
			options,
			fetcher,
			fetching,
			onValueChange,
			variant,
			defaultValue = [],
			placeholder = "Select options",
			animation = 0,
			maxCount = 20,
			modalPopover = false,
			//   asChild = false,
			className,
			...props
		},
		ref,
	) => {
		const [selectedValues, setSelectedValues] = React.useState(defaultValue);
		const [isPopoverOpen, setIsPopoverOpen] = React.useState(false);
		const [isAnimating, setIsAnimating] = React.useState(false);

		const handleSearchQueryChange = (event: string) => {
			if (fetcher) {
				fetcher(event);
			}
			const newSelectedValues = [...selectedValues];
			setSelectedValues(newSelectedValues);
		};

		const toggleOptionGenre = (option: ILocationMinimal) => {
			const newSelectedValues = selectedValues.some(
				(value) => value.placeId === option.placeId,
			)
				? selectedValues.filter((value) => value.placeId !== option.placeId)
				: [...selectedValues, option];
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues);
		};

		const handleClear = () => {
			setSelectedValues([]);
			onValueChange([]);
		};

		const handleTogglePopover = () => {
			setIsPopoverOpen((prev) => !prev);
		};

		const clearExtraOptions = () => {
			const newSelectedValues = selectedValues.slice(0, maxCount);
			setSelectedValues(newSelectedValues);
			onValueChange(newSelectedValues);
		};

		return (
			<Popover
				open={isPopoverOpen}
				onOpenChange={setIsPopoverOpen}
				modal={modalPopover}
			>
				<PopoverTrigger asChild>
					<Button
						ref={ref}
						{...props}
						onClick={handleTogglePopover}
						className={cn(
							"flex w-full p-1 rounded-md border min-h-10 h-auto items-center justify-between bg-inherit hover:bg-inherit [&_svg]:pointer-events-auto",
							className,
						)}
					>
						{selectedValues.length > 0 ? (
							<div className="flex justify-between items-center w-full">
								<div className="flex flex-wrap items-center">
									{selectedValues.slice(0, maxCount).map((value) => {
										return (
											<Badge
												key={value.placeId}
												className={cn(
													isAnimating ? "animate-bounce" : "",
													multiSelectVariants({ variant }),
												)}
												style={{ animationDuration: `${animation}s` }}
											>
												{value?.address}
												<XCircle
													className="ml-2 w-4 h-4 cursor-pointer"
													onClick={(event) => {
														event.stopPropagation();
														toggleOptionGenre({
															...value,
														});
													}}
												/>
											</Badge>
										);
									})}
									{selectedValues.length > maxCount && (
										<Badge
											className={cn(
												"bg-transparent text-foreground border-foreground/1 hover:bg-transparent",
												isAnimating ? "animate-bounce" : "",
												multiSelectVariants({ variant }),
											)}
											style={{ animationDuration: `${animation}s` }}
										>
											{`+ ${selectedValues.length - maxCount} more`}
											<XCircle
												className="ml-2 w-4 h-4 cursor-pointer"
												onClick={(event) => {
													event.stopPropagation();
													clearExtraOptions();
												}}
											/>
										</Badge>
									)}
								</div>
								<div className="flex justify-between items-center">
									<XIcon
										className="mx-2 h-4 text-secondary cursor-pointer"
										onClick={(event) => {
											event.stopPropagation();
											handleClear();
										}}
									/>
									<Separator
										orientation="vertical"
										className="flex h-full min-h-6"
									/>
									<div className="px-3">
										<ArrowBottomIcon />
									</div>
								</div>
							</div>
						) : (
							<div className="flex justify-between items-center mx-auto w-full">
								<span className="mx-3 text-secondary text-sm">
									{placeholder}
								</span>
								<div className="px-3">
									<ArrowBottomIcon />
								</div>
							</div>
						)}
					</Button>
				</PopoverTrigger>
				<PopoverContent
					className="bg-background p-0 border border-border w-full font-semibold text-white"
					align="start"
					onEscapeKeyDown={() => setIsPopoverOpen(false)}
				>
					<Command className="bg-inherit text-white overflow-hidden">
						<Input
							className="border-0 rounded-none placeholder:text-secondary"
							placeholder="Search for a location"
							onChange={(event) => {
								handleSearchQueryChange(event.target.value);
							}}
						/>
						<CommandList>
							{fetching ? (
								<CommandList className="flex justify-center items-center w-full">
									<CommandEmpty>
										<Spinner width={20} />
									</CommandEmpty>
								</CommandList>
							) : (
								<CommandList>
									<CommandGroup>
										{options.map((option) => {
											const isSelected = selectedValues.some(
												(value) => value.placeId === option.placeId,
											);
											return (
												<CommandItem
													key={option.placeId}
													onSelect={() =>
														toggleOptionGenre({
															placeId: option.placeId,
															address: option.address,
														})
													}
													className={cn(
														"hover:text-black cursor-pointer",
														isSelected ? "text-primary" : "text-primary",
													)}
												>
													<div
														className={cn(
															"mr-2 flex h-4 w-4 items-center justify-center rounded-sm border border-primary",
															isSelected
																? "bg-primary text-white"
																: "opacity-50 [&_svg]:invisible",
														)}
													>
														<CheckIcon className="w-4 h-4 text-black" />
													</div>

													{option.address}
												</CommandItem>
											);
										})}
										{/* {options.length === 0 && (
											<div className="py-5 w-full font-semibold text-center text-primary text-xs">
												No result found
											</div>
										)} */}
									</CommandGroup>
									<CommandSeparator />
									<CommandGroup>
										<div className="flex justify-between items-center">
											{selectedValues.length > 0 && (
												<>
													<CommandItem
														onSelect={handleClear}
														className="flex-1 justify-center text-primary cursor-pointer"
													>
														Clear
													</CommandItem>
													<Separator
														orientation="vertical"
														className="flex h-full min-h-6"
													/>
												</>
											)}
											<CommandItem
												onSelect={() => setIsPopoverOpen(false)}
												className="flex-1 justify-center max-w-full text-primary cursor-pointer"
											>
												Close
											</CommandItem>
										</div>
									</CommandGroup>
								</CommandList>
							)}
						</CommandList>
					</Command>
				</PopoverContent>
				{animation > 0 && selectedValues.length > 0 && (
					<WandSparkles
						className={cn(
							"cursor-pointer my-2 text-foreground bg-background w-3 h-3",
							isAnimating ? "" : "text-muted-foreground",
						)}
						onClick={() => setIsAnimating(!isAnimating)}
					/>
				)}
			</Popover>
		);
	},
);

MultiSelectLocationSearch.displayName = "MultiSelectLocationSearch";
